import { Modal, Table, Input, Tooltip, Tag, message, Button } from 'antd'
import React, { useRef, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { CloseOutlined, DownloadOutlined } from '@ant-design/icons'
import { http } from '@services/http'
import { useQuery } from '@tanstack/react-query'
import moment from 'moment'
import { numberWithSpacesIntl } from '@utils/numberWithSpaces'
import ViewIcon from '@assets/icons/viewSvg'

const ClientsDebtSalesModal = ({ visible, data, onClose, tableData }) => {
	const today = moment().format('YYYY-MM-DD')

	const { t } = useTranslation()

	const [startDate, setStartDate] = useState('')
	const [endDate, setEndDate] = useState(today)
	const [cardCode, setCardCode] = useState('')
	const [slpCode, setSlpCode] = useState('')
	const [loading, setLoading] = useState(false)
	const [pageSize, setPageSize] = useState(1000)
	const [currentPage, setCurrentPage] = useState(0)

	const handleStartDate = (e) => {
		setStartDate(e.target.value)
	}
	const handleEndDate = (e) => {
		setEndDate(e.target.value)
	}

	useEffect(() => {
		setSlpCode(tableData?.slpCode)
		setCardCode(data?.cardCode)
	}, [data, visible, tableData])

	const fetchSlpDebtors = async ({ queryKey }) => {
		const [_, slpCode, cardCode, startDate, endDate, skip, pageSize] =
			queryKey
		try {
			const { data } = await http.get(
				`api/salesdata/manager-debitors-documents?cardCode=${cardCode}&slpCode=${slpCode}&startDate=${startDate}&endDate=${endDate}&skip=${skip}&pageSize=${pageSize}`,
			)
			return data
		} catch (error) {
			console.error('Error fetching data:', error)
		}
	}

	const { data: slpDebtorsData = [], isLoading } = useQuery({
		queryKey: [
			'slpDebtorsSales',
			slpCode,
			cardCode,
			startDate,
			endDate,
			currentPage,
			pageSize,
		],
		queryFn: fetchSlpDebtors,
		enabled: !!slpCode && !!cardCode,
		keepPreviousData: true,
		refetchOnWindowFocus: true,
		refetchOnMount: true,
		refetchOnReconnect: true,
	})

	const columnsDebtors = [
		{
			title: t('docNum'),
			dataIndex: 'docNum',
			key: 'docNum',
		},
		{
			title: t('docDate'),
			dataIndex: 'docDate',
			key: 'docDate',
			render: (text) => {
				return `${moment(text).format('DD.MM.YYYY')}`
			},
		},

		{
			title: t('totalInvoiceAmount'),
			dataIndex: 'totalInvoiceAmount',
			key: 'totalInvoiceAmount',
			render: (text, record) => {
				const formattedText = new Intl.NumberFormat('fr-FR').format(
					text,
				)
				return `${formattedText} `
			},
		},
		{
			title: t('totalPaidAmount'),
			dataIndex: 'totalPaidAmount',
			key: 'totalPaidAmount',
			render: (text, record) => {
				const formattedText = new Intl.NumberFormat('fr-FR').format(
					text,
				)
				return `${formattedText} `
			},
		},
		{
			title: t('debtUzs'),
			dataIndex: 'totalOutstandingAmount',
			key: 'totalOutstandingAmount',
			render: (text, record) => {
				const formattedText = new Intl.NumberFormat('fr-FR').format(
					text,
				)
				return `${formattedText} `
			},
		},
		{
			title: t('daysSinceFirstInvoice'),
			dataIndex: 'daysFromFirstInvoice',
			key: 'daysFromFirstInvoice',
		},
	]

	const handleViewClick = (record) => {
		setSelectedRowData(record)
		setViewModalOpen(true)
	}

	const onCloseModal = () => {
		setStartDate('')
		setEndDate(today)
		onClose()
		setCardCode('')
		setSlpCode('')
	}

	return (
		<Modal
			title={null}
			visible={visible}
			onCancel={onCloseModal}
			footer={null}
			closeIcon={<CloseOutlined />}
			width="85%"
			style={{ height: '85%' }}
			bodyStyle={{ height: 'calc(100% - 55px)', overflowY: 'auto' }}
		>
			<div className="w-full px-5">
				<div className="mt-5 flex flex-col items-end justify-between sm:flex-row">
					<span className="font-bold text-[16px]">
						{t('clientsDebt')} : {data?.cardName}
					</span>
					<span className="font-bold text-[16px]">
						{t('totalDebt')} :{' '}
						{numberWithSpacesIntl(
							slpDebtorsData?.reduce(
								(acc, cur) => acc + cur.totalOutstandingAmount,
								0,
							),
						)}
					</span>
				</div>
				<div className="flex gap-5 mt-5">
					<div className="flex flex-col gap-1">
						<p>{t('startDate')}</p>
						<Input
							type="date"
							value={startDate}
							className="w-[150px]"
							onChange={(e) => {
								setStartDate(e.target.value)
							}}
							max={endDate}
						/>
					</div>
					<div className="flex flex-col gap-1">
						<p>{t('endDate')}</p>
						<Input
							type="date"
							value={endDate}
							className="w-[150px]"
							onChange={(e) => {
								setEndDate(e.target.value)
							}}
							min={startDate}
							max={today}
						/>
					</div>
				</div>
				<div className="mt-10 flex w-full flex-col gap-5">
					<Table
						columns={columnsDebtors}
						dataSource={slpDebtorsData}
						pagination={false}
						loading={isLoading}
						className="border-1 border-black"
					/>
				</div>
			</div>
		</Modal>
	)
}

export default ClientsDebtSalesModal
